/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import moment from 'moment'; // eslint-disable-line import/named

import dateMath from '@elastic/datemath';
import { EuiDatePicker } from '../../date_picker';
import { EuiFormRow, EuiFieldText, EuiFormLabel } from '../../../form';
import { EuiI18n } from '../../../i18n';
export class EuiAbsoluteTab extends Component {
  constructor(props) {
    super(props);
    const parsedValue = dateMath.parse(props.value, {
      roundUp: props.roundUp
    });
    const valueAsMoment = parsedValue && parsedValue.isValid() ? parsedValue : moment();
    const textInputValue = valueAsMoment.locale(this.props.locale || 'en').format(this.props.dateFormat);
    this.state = {
      isTextInvalid: false,
      textInputValue,
      valueAsMoment
    };
  }
  handleChange = (date, event) => {
    const {
      onChange
    } = this.props;
    if (date === null) {
      return;
    }
    onChange(date.toISOString(), event);
    const valueAsMoment = moment(date);
    this.setState({
      valueAsMoment,
      textInputValue: valueAsMoment.format(this.props.dateFormat),
      isTextInvalid: false
    });
  };
  handleTextChange = event => {
    const {
      onChange
    } = this.props;
    const valueAsMoment = moment(event.target.value, this.props.dateFormat, true);
    const dateIsValid = valueAsMoment.isValid();
    if (dateIsValid) {
      onChange(valueAsMoment.toISOString(), event);
    }
    this.setState({
      textInputValue: event.target.value,
      isTextInvalid: !dateIsValid,
      valueAsMoment: dateIsValid ? valueAsMoment : null
    });
  };
  render() {
    const {
      dateFormat,
      timeFormat,
      locale,
      utcOffset,
      labelPrefix
    } = this.props;
    const {
      valueAsMoment,
      isTextInvalid,
      textInputValue
    } = this.state;
    return <div>
        <EuiDatePicker inline showTimeSelect shadow={false} selected={valueAsMoment} onChange={this.handleChange} dateFormat={dateFormat} timeFormat={timeFormat} locale={locale} utcOffset={utcOffset} />
        <EuiI18n token="euiAbsoluteTab.dateFormatError" default="Expected format: {dateFormat}" values={{
        dateFormat
      }}>
          {dateFormatError => <EuiFormRow className="euiSuperDatePicker__absoluteDateFormRow" isInvalid={isTextInvalid} error={isTextInvalid ? dateFormatError : undefined}>
              <EuiFieldText compressed isInvalid={isTextInvalid} value={textInputValue} onChange={this.handleTextChange} data-test-subj={'superDatePickerAbsoluteDateInput'} prepend={<EuiFormLabel>{labelPrefix}</EuiFormLabel>} />
            </EuiFormRow>}
        </EuiI18n>
      </div>;
  }
}
EuiAbsoluteTab.propTypes = {
  dateFormat: PropTypes.string.isRequired,
  timeFormat: PropTypes.string.isRequired,
  locale: PropTypes.any,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  roundUp: PropTypes.bool.isRequired,
  position: PropTypes.oneOf(["start", "end"]).isRequired,
  labelPrefix: PropTypes.string.isRequired,
  utcOffset: PropTypes.number
};
try {
  EuiAbsoluteTab.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
    description: '',
    displayName: 'EuiAbsoluteTab',
    methods: [],
    props: {
      dateFormat: {
        defaultValue: null,
        description: '',
        name: 'dateFormat',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      timeFormat: {
        defaultValue: null,
        description: '',
        name: 'timeFormat',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      locale: {
        defaultValue: null,
        description: '',
        name: 'locale',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: false,
        type: {
          name: 'LocaleSpecifier'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: '(date: string, event?: React.SyntheticEvent<any>) => void'
        }
      },
      roundUp: {
        defaultValue: null,
        description: '',
        name: 'roundUp',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      position: {
        defaultValue: null,
        description: '',
        name: 'position',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'enum',
          raw: '"end" | "start"',
          value: [{
            value: '"end"'
          }, {
            value: '"start"'
          }]
        }
      },
      labelPrefix: {
        defaultValue: null,
        description: '',
        name: 'labelPrefix',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      utcOffset: {
        defaultValue: null,
        description: '',
        name: 'utcOffset',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      }
    },
    extendedInterfaces: ['EuiAbsoluteTabProps']
  };
} catch (e) {}